import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { db } from "../../lib/firebase";
import { useTranslation } from "react-i18next";
import { updateProfile as updateAuthProfile } from "firebase/auth";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import LocationPicker from "../LocationPicker";
import { AvatarUploader } from "../AvatarUploader";
import { uploadImageToImgbb } from "../../utils/uploadImageToImgbb";
import { createHandleFromEmail, normalizeUsername } from "../../utils/userIdentity";
import {
  PROFILE_VALIDATION,
  PROFILE_PUBLIC_FIELDS,
  DEFAULT_PUBLIC_FIELDS,
  DEFAULT_LOCATION,
  BIO_CONFIG,
  FORM_FIELDS_CONFIG,
} from "../../constants/profileConstants";

const defaultProfileState = {
  username: "",
  name: "",
  email: "",
  phone: "",
  avatar: "",
  bio: "",
  location: { ...DEFAULT_LOCATION },
  publicFields: { ...DEFAULT_PUBLIC_FIELDS },
  profilePublic: true,
};

export default function ProfileEditForm({
  profile,
  updateProfile,
  signedIn,
  currentUser,
  onSubmitSuccess,
  onCancel,
  compact = false,
  showCancel = false,
  initialSection = "all",
  saveLabel,
  savingLabel,
}) {
  const user = currentUser;
  const { t, ready: i18nReady } = useTranslation(["profile", "common"]);

  const safeT = useMemo(() => {
    return (key, fallback = key, options) => {
      if (!i18nReady) return fallback;
      const result = t(key, options);
      return result === key ? fallback : result;
    };
  }, [i18nReady, t]);

  const [localProfile, setLocalProfile] = useState(defaultProfileState);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!profile) return;
    setLocalProfile((prev) => ({
      ...prev,
      ...profile,
      username: profile.username || "",
      name: profile.name || profile.displayName || profile.username || "",
      publicFields: {
        ...DEFAULT_PUBLIC_FIELDS,
        ...(profile.publicFields || {}),
      },
      location: {
        ...DEFAULT_LOCATION,
        ...(profile.location || {}),
      },
    }));
  }, [profile]);

  useEffect(() => {
    if (initialSection !== "photo") return;
    const timeout = setTimeout(() => {
      const el = document.getElementById("profile-edit-avatar-uploader");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);

    return () => clearTimeout(timeout);
  }, [initialSection]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalProfile((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleLocationChange = (location) => {
    setLocalProfile((prev) => ({ ...prev, location }));
  };

  const handleAvatarUpload = (url) => {
    setLocalProfile((prev) => ({ ...prev, avatar: url }));
  };

  const validateForm = () => {
    const newErrors = {};

    const cleanedUsername = normalizeUsername(localProfile.username);
    if (!cleanedUsername) {
      newErrors.username = safeT("profile.edit.fields.usernameRequired", "Username is required");
    } else if (cleanedUsername.length < 3) {
      newErrors.username = safeT("profile.edit.validation.usernameLength", "Username must be at least 3 characters");
    }

    if (!localProfile.name?.trim()) {
      newErrors.name = safeT("profile.edit.fields.nameRequired", "Name is required");
    } else if (localProfile.name.length > PROFILE_VALIDATION.NAME.MAX_LENGTH) {
      newErrors.name = safeT("profile.edit.validation.maxLength", "Maximum {{max}} characters allowed", {
        max: PROFILE_VALIDATION.NAME.MAX_LENGTH,
      });
    } else if (!PROFILE_VALIDATION.NAME.PATTERN.test(localProfile.name)) {
      newErrors.name = safeT("profile.edit.fields.nameInvalid", "Please enter a valid name");
    }

    if (localProfile.email && !PROFILE_VALIDATION.EMAIL.PATTERN.test(localProfile.email)) {
      newErrors.email = safeT("profile.edit.validation.invalidEmail", "Please enter a valid email address");
    }

    if (localProfile.phone && !PROFILE_VALIDATION.PHONE.PATTERN.test(localProfile.phone)) {
      newErrors.phone = safeT("profile.edit.validation.invalidPhone", "Please enter a valid phone number");
    }

    if (localProfile.bio && localProfile.bio.length > BIO_CONFIG.MAX_LENGTH) {
      newErrors.bio = safeT("profile.edit.fields.bioLength", "Bio cannot exceed {{max}} characters", {
        max: BIO_CONFIG.MAX_LENGTH,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!signedIn || !currentUser) {
      setErrors({ form: safeT("profile.edit.messages.signInRequired", "Please sign in to edit your profile") });
      return;
    }

    if (currentUser.uid !== user?.uid) {
      setErrors({ form: safeT("profile.edit.messages.unauthorized", "You are not authorized to edit this profile") });
      return;
    }

    setSubmitting(true);
    setSuccessMessage(null);

    try {
      const normalizedUsername = normalizeUsername(localProfile.username) || createHandleFromEmail(currentUser.email, currentUser.uid);

      const usernameQuery = query(
        collection(db, "users"),
        where("usernameLower", "==", normalizedUsername.toLowerCase()),
        limit(1)
      );
      const usernameSnap = await getDocs(usernameQuery);
      if (!usernameSnap.empty && usernameSnap.docs[0].id !== currentUser.uid) {
        setErrors({ username: safeT("profile.edit.validation.usernameTaken", "This username is already taken") });
        setSubmitting(false);
        return;
      }

      const fullName = (localProfile.name || "").trim();
      const displayName = normalizedUsername;
      const avatarUrl = localProfile.avatar || localProfile.photoURL || "";

      const profileData = {
        ...localProfile,
        username: normalizedUsername,
        usernameLower: normalizedUsername.toLowerCase(),
        displayName,
        name: fullName || normalizedUsername,
        avatar: avatarUrl,
        photoURL: avatarUrl,
        publicFields: {
          [PROFILE_PUBLIC_FIELDS.EMAIL]: !!localProfile.publicFields?.email,
          [PROFILE_PUBLIC_FIELDS.PHONE]: !!localProfile.publicFields?.phone,
          [PROFILE_PUBLIC_FIELDS.AVATAR]: !!localProfile.publicFields?.avatar,
          [PROFILE_PUBLIC_FIELDS.LOCATION]: !!localProfile.publicFields?.location,
          [PROFILE_PUBLIC_FIELDS.BIO]: !!localProfile.publicFields?.bio,
        },
        updatedAt: new Date(),
      };

      await updateProfile(profileData);
      await updateAuthProfile(currentUser, {
        displayName,
        photoURL: avatarUrl || null,
      });

      setSuccessMessage(safeT("profile.edit.messages.updateSuccess", "Profile updated successfully!"));
      if (onSubmitSuccess) onSubmitSuccess(currentUser.uid);
    } catch (err) {
      setErrors({ form: safeT("profile.edit.messages.updateFailed", "Failed to update profile. Please try again.") });
    } finally {
      setSubmitting(false);
    }
  };

  const cardClass = compact
    ? "bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4"
    : "bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-neutral-700 p-4 sm:p-6";

  return (
    <form id="edit-profile-form" onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {errors.form && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{errors.form}</p>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-700 dark:text-green-300 text-sm">{successMessage}</p>
        </div>
      )}

      <div className={cardClass}>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {safeT("profile.edit.sections.basicInfo.title", "Basic Information")}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT("profile.edit.fields.username", "Username")} *
            </label>
            <input
              name="username"
              value={localProfile.username || ""}
              onChange={handleChange}
              type="text"
              required
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm ${
                errors.username ? "border-red-500" : "border-gray-300 dark:border-neutral-600"
              } bg-white dark:bg-neutral-700 text-gray-900 dark:text-white`}
              placeholder={safeT("profile.edit.fields.usernamePlaceholder", "Choose a username")}
              aria-label={safeT("profile.edit.fields.username", "Username")}
            />
            {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.NAME.labelKey, "Full Name")} *
            </label>
            <input
              name={FORM_FIELDS_CONFIG.NAME.name}
              value={localProfile.name || localProfile.displayName || ""}
              onChange={handleChange}
              type={FORM_FIELDS_CONFIG.NAME.type}
              required={FORM_FIELDS_CONFIG.NAME.required}
              maxLength={PROFILE_VALIDATION.NAME.MAX_LENGTH}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm ${
                errors.name ? "border-red-500" : "border-gray-300 dark:border-neutral-600"
              } bg-white dark:bg-neutral-700 text-gray-900 dark:text-white`}
              placeholder={safeT(FORM_FIELDS_CONFIG.NAME.placeholderKey, "Enter your full name")}
              aria-label={safeT(FORM_FIELDS_CONFIG.NAME.labelKey, "Full Name")}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.BIO.labelKey, "Bio")}
            </label>
            <textarea
              name={FORM_FIELDS_CONFIG.BIO.name}
              value={localProfile.bio || ""}
              onChange={handleChange}
              rows={FORM_FIELDS_CONFIG.BIO.rows}
              maxLength={BIO_CONFIG.MAX_LENGTH}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm ${
                errors.bio ? "border-red-500" : "border-gray-300 dark:border-neutral-600"
              } bg-white dark:bg-neutral-700 text-gray-900 dark:text-white`}
              placeholder={safeT(FORM_FIELDS_CONFIG.BIO.placeholderKey, "Tell people about yourself")}
              aria-label={safeT(FORM_FIELDS_CONFIG.BIO.labelKey, "Bio")}
            />
            <div className="flex justify-end text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{localProfile.bio?.length || 0}/{BIO_CONFIG.MAX_LENGTH}</span>
            </div>
            {errors.bio && <p className="text-red-500 text-xs mt-1">{errors.bio}</p>}
          </div>

          <div id="profile-edit-avatar-uploader">
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.AVATAR.labelKey, "Profile photo")}
            </label>
            <AvatarUploader
              initialUrl={localProfile.avatar || localProfile.photoURL}
              onUpload={handleAvatarUpload}
              ariaLabel={safeT("profile.edit.aria.uploadAvatar", "Upload profile photo")}
              uploadFn={(file) => uploadImageToImgbb(file, { target: "profile" })}
              label={safeT("profile.edit.fields.avatar", "Profile photo")}
              resizedHint={safeT("profile.edit.fields.imageInfo", "Images are resized on-device to save bandwidth.")}
              uploadingLabel={safeT("profile.edit.messages.uploading", "Uploading...")}
              choosePhotoLabel={safeT("profile.edit.fields.photoChoose", "Choose photo")}
              changePhotoLabel={safeT("profile.edit.fields.photoChange", "Change photo")}
              currentPhotoLabel={safeT("profile.edit.fields.photoCurrent", "Current profile photo")}
              noFileLabel={safeT("profile.edit.fields.photoNoFile", "No file selected")}
              showLabel={false}
              compact={true}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              {safeT("profile.edit.fields.avatarHelpText", "Upload a clear photo of yourself")}
            </p>
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {safeT("profile.edit.sections.contactInfo.title", "Contact Information")}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.EMAIL.labelKey, "Email")}
            </label>
            <input
              name={FORM_FIELDS_CONFIG.EMAIL.name}
              value={localProfile.email || ""}
              onChange={handleChange}
              type={FORM_FIELDS_CONFIG.EMAIL.type}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm ${
                errors.email ? "border-red-500" : "border-gray-300 dark:border-neutral-600"
              } bg-white dark:bg-neutral-700 text-gray-900 dark:text-white`}
              placeholder={safeT(FORM_FIELDS_CONFIG.EMAIL.placeholderKey, "you@example.com")}
              aria-label={safeT(FORM_FIELDS_CONFIG.EMAIL.labelKey, "Email")}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.PHONE.labelKey, "Phone Number")}
            </label>
            <input
              name={FORM_FIELDS_CONFIG.PHONE.name}
              value={localProfile.phone || ""}
              onChange={handleChange}
              type={FORM_FIELDS_CONFIG.PHONE.type}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-600 focus:border-green-600 text-sm ${
                errors.phone ? "border-red-500" : "border-gray-300 dark:border-neutral-600"
              } bg-white dark:bg-neutral-700 text-gray-900 dark:text-white`}
              placeholder={safeT(FORM_FIELDS_CONFIG.PHONE.placeholderKey, "+250 7XX XXX XXX")}
              aria-label={safeT(FORM_FIELDS_CONFIG.PHONE.labelKey, "Phone Number")}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label className="block font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">
              {safeT(FORM_FIELDS_CONFIG.LOCATION.labelKey, "Location")}
            </label>
            <LocationPicker value={localProfile.location} onChange={handleLocationChange} compact={true} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {showCancel && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-sm font-medium"
          >
            {safeT("profile.edit.buttons.cancel", "Cancel")}
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          {submitting
            ? (savingLabel || safeT("profile.edit.buttons.saving", "Saving..."))
            : (saveLabel || safeT("profile.edit.buttons.save", "Save changes"))}
        </button>
      </div>
    </form>
  );
}

ProfileEditForm.propTypes = {
  profile: PropTypes.object,
  updateProfile: PropTypes.func.isRequired,
  signedIn: PropTypes.bool,
  currentUser: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  compact: PropTypes.bool,
  showCancel: PropTypes.bool,
  initialSection: PropTypes.oneOf(["all", "photo"]),
  saveLabel: PropTypes.string,
  savingLabel: PropTypes.string,
};
